import React, { useEffect, useState } from "react"
import type { OverlayRenderProps } from "react-photo-view/dist/types"
import { isIOS, isMobile } from "../../utils/detectDevice"
import { Slider } from "@blueprintjs/core"
import { Button } from "@/components/ui/button"
import { DownloadIcon } from "@radix-ui/react-icons"
import type { Message } from "../../reducers/messagesSlice"
import useHasFileBeenShared from "../../hooks/useHasFileBeenShared"
import useHandleDownload from "../../hooks/useHandleDownload"
import useHandleShareLink from "../../hooks/useHandleShareLink"
import useDownloadFileName from "../../hooks/useDownloadFileName"
import classNames from "classnames"
import ImageShareButton from "../photoView/ImageShareButton"
import MessageHelpers from "../../lib/messageHelpers"
import CMRMetaDataDrawer from "@/components/cmr/CMRMetaDataDrawer"
import { mapFileUrlToSameOrigin } from "../message/utils/mapFileToSameOrigin"

export type PDFTopToolbarProps = OverlayRenderProps & {
  totalPages: number
  PDFMessage: Message
  onIndexChange: (index: number) => void
}

export const PDFBottomToolbar = ({
  totalPages,
  index,
  onIndexChange,
  PDFMessage,
}: PDFTopToolbarProps) => {
  const hasFileBeenShared = useHasFileBeenShared(PDFMessage)
  const handleShareLink = useHandleShareLink(PDFMessage)
  const handleDownload = useHandleDownload()
  const downloadFileName = useDownloadFileName(PDFMessage)
  const [sliderIndex, setSliderIndex] = useState(1)

  useEffect(() => {
    setSliderIndex(index)
  }, [index])

  const showSlider = !isMobile && totalPages > 50

  return (
    <div
      className={classNames(
        "absolute bottom-0 right-0 z-50 flex w-full flex-col justify-center gap-2 bg-black/50 p-2 text-sm text-slate-300",
        { "min-h-20": isIOS },
      )}
    >
      {/* Render slider for non-mobile devices with more than 50 pages */}
      {showSlider && (
        <div className="mx-32">
          <Slider
            className="w-full"
            min={0}
            labelValues={[1, Math.floor(totalPages / 2), totalPages - 1]}
            labelRenderer={(value) => <span>{value + 1}</span>}
            value={sliderIndex}
            initialValue={1}
            onChange={setSliderIndex}
            onRelease={onIndexChange}
            max={totalPages - 1}
          />
        </div>
      )}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-md bg-black/60 p-2 text-white mix-blend-color">
        {MessageHelpers.timestamp(PDFMessage)}
      </div>
      <div className="flex flex-row justify-end gap-2">
        <div className="flex-1" />
        {!isMobile && PDFMessage && handleDownload && downloadFileName && (
          <a
            href={PDFMessage.url}
            download
            onClick={(e) => {
              e.preventDefault()
              handleDownload(PDFMessage.url!, downloadFileName!)
            }}
          >
            <Button variant="ghost" size="icon">
              <DownloadIcon className="h-4 w-4" />
            </Button>
          </a>
        )}
        {isMobile && PDFMessage.url && (
          <a
            href={mapFileUrlToSameOrigin(PDFMessage.url)}
            download
            target="_blank"
            rel="noreferrer"
          >
            <Button variant="ghost" size="icon">
              <DownloadIcon className="h-4 w-4" />
            </Button>
          </a>
        )}
        {PDFMessage && handleShareLink && downloadFileName && (
          <ImageShareButton
            hasFileBeenShared={hasFileBeenShared}
            handleShareLink={handleShareLink}
            downloadFileName={downloadFileName}
          />
        )}
        <CMRMetaDataDrawer message={PDFMessage} />
      </div>
    </div>
  )
}
